import { ConsentSettings } from "../consentSettings";
import { ContentInteraction } from "../interfaces";
import * as floodlight_test_template from "../../floodlight_template/test/tags_data_compact.json";
import * as floodlight_prod_template from "../../floodlight_template/prod/tags_data_compact.json";
import {
  AnalyticsProvider,
  ConversionEventDetail
} from "./analyticsProviderBase";

export class Floodlight extends AnalyticsProvider {
  floodlight_mapping_template;

  constructor(use_floodlight_test_template: boolean) {
    super();

    this.name = "Floodlight";

    this.floodlight_mapping_template = JSON.stringify(
      use_floodlight_test_template
        ? floodlight_test_template
        : floodlight_prod_template
    );
  }

  start(_consent_settings: ConsentSettings): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true // _consent_settings.targeting == true;

    if (this.has_consent_to_run) {
      const market = sv.get_platform_market();
      const country = sv.get_user_country();
      for (const advertiser of JSON.parse(this.floodlight_mapping_template)[
        "advertisers"
      ]) {
        const advertiser_markets = new Set(Object.keys(advertiser[1]));
        const advertiser_countries = new Set(
          Object.values(advertiser[1]).flat()
        );
        if (advertiser_countries.size === 0) {
          if (advertiser_markets.has(market)) {
            wspr.P("gtag_init", ["config", `DC-${advertiser[0]}`]);
          }
        } 
        else if (advertiser_markets.has(market) && advertiser_countries.has(country)) {
          wspr.P("gtag_init", ["config", `DC-${advertiser[0]}`]);
        }
      }
    }

    this.has_started = true;
  }

  conversion_triggered(_conversion_event_detail: ConversionEventDetail): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true // _consent_settings.targeting == true;

    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_conversion();
    }

    this.log("Floodlight Conversions web-request-submitted started");
    
    const floodlight_map = this.get_floodlight_mapping(
      _conversion_event_detail.wrid,
      _conversion_event_detail.uwrid,
      _conversion_event_detail.brochure,
      _conversion_event_detail.is_agent,
      _conversion_event_detail.request_type,
      _conversion_event_detail?.purchase?.revenue,
      _conversion_event_detail?.purchase?.transaction_id
    );

    if (floodlight_map.conversions) {
      const conversion_tags = floodlight_map["conversions"][_conversion_event_detail.request_type];

      if (conversion_tags === undefined) {
        this.log("No floodlight conversion found for request_type: " + _conversion_event_detail.request_type);
      } else {
        this.log(`Conversion tags found: ${JSON.stringify(conversion_tags)}`);
        
        for (let i = 0, l = conversion_tags.length; i < l; i++) {
          const allowed_markets = this.get_advertiser_markets(conversion_tags[i][0]);
          const allowed_countries = this.get_advertiser_countries(conversion_tags[i][0]);

          this.log(`Allowed markets: ${Array.from(allowed_markets)}`);
          this.log(`Allowed countries: ${Array.from(allowed_countries)}`);

          const input = {
            allowed_markets,
            allowed_countries,
            advertiser_id: this.get_advertiser_id(conversion_tags[i][0]),
            count_method: this.get_floodlight_count_method(conversion_tags[i][3]),
            type: conversion_tags[i][1],
            cat: conversion_tags[i][2],
            u_array: conversion_tags[i][4],
            value: _conversion_event_detail?.purchase?.revenue,
            transaction_id: _conversion_event_detail?.purchase?.transaction_id
          };

          if (allowed_markets.has(sv.get_platform_market()) && 
              (allowed_countries.size === 0 || allowed_countries.has(sv.get_user_country()))) {
            this.log(`Triggering floodlight for advertiser ${conversion_tags[i][0]}`);
            this.trigger_floodlight_tag(input);
          } else {
            this.log(`Market or country not matched. Market: ${sv.get_platform_market()}, Country: ${sv.get_user_country()}`);
          }
        }
      }
    }
    
    this.log("Floodlight Conversions web-request-submitted success");
  }

  interaction_triggered(_data_content_attribs: ContentInteraction): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true // _consent_settings.targeting == true;

    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_interaction();
    }
    this.log("this.content_interaction_floodlight() start");
    const floodlight_clicks = this.get_floodlight_mapping(undefined, undefined)[
      "clicks"
    ];
    const page = this.get_clean_page();

    for (let i = 0, l = floodlight_clicks.length; i < l; i++) {
      const click_breadcrumb = floodlight_clicks[i][0].split("*");
      const tags = floodlight_clicks[i][1];

      this.log(`Breadcrumb: ${click_breadcrumb}`);
      this.log(`Data content name: ${_data_content_attribs["data-content-name"]}`);
      this.log(`Data content piece: ${_data_content_attribs["data-content-piece"]}`);

      if (
        (click_breadcrumb[0] == "" || page.startsWith(click_breadcrumb[0])) &&
        (click_breadcrumb[1] == 0 ||
          (click_breadcrumb[1] && page == click_breadcrumb[0])) &&
        (click_breadcrumb[2] == "" ||
          click_breadcrumb[2].includes(
            _data_content_attribs["data-content-name"]
          )) &&
        (click_breadcrumb[3] == "" ||
          click_breadcrumb[3] == _data_content_attribs["data-content-piece"])
      ) {
        for (let ii = 0, ll = tags.length; ii < ll; ii++) {
          this.log(`Checking advertiser for click event: ${tags[ii][0]}`);

          this.trigger_floodlight_tag({
            allowed_markets: this.get_advertiser_markets(tags[ii][0]),
            allowed_countries: this.get_advertiser_countries(tags[ii][0]),
            advertiser_id: this.get_advertiser_id(tags[ii][0]),
            count_method: this.get_floodlight_count_method(tags[ii][3]),
            type: tags[ii][1],
            cat: tags[ii][2]
          });
        }
      }
    }
    this.log("this.content_interaction_floodlight() success");
  }

  pageload_triggered(): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true // _consent_settings.targeting == true;
    
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_pageload();
    }
    this.initialise_subscribers();

    const url_parts = document.URL.split("/");
    url_parts.splice(0, 2);

    this.pageview_floodlight(url_parts.join("/"));
  }

  trigger_floodlight_tag({
    allowed_markets,
    allowed_countries,
    advertiser_id,
    count_method,
    type,
    cat,
    u_array,
    value,
    transaction_id
  }: {
    allowed_markets: Set<string>;
    allowed_countries: Set<string>;
    advertiser_id?: number | string;
    count_method: string;
    type: string;
    cat: string;
    u_array?: { [index: number]: string | number | boolean };
    value?: number;
    transaction_id?: string;
  }): void {
    const market = sv.get_platform_market();
    const country = sv.get_user_country();

    if (
      (allowed_countries.size === 0 && allowed_markets.has(market)) ||
      (allowed_countries.has(country) && allowed_markets.has(market)) 
    ) {
      this.execute_floodlight(advertiser_id, type, cat, count_method, u_array, value, transaction_id);
    } else {
      this.debug(`Floodlight tag not valid for this market (${market}) or country (${country}).`);
    }
  }

  private execute_floodlight(
    advertiser_id?: number | string,
    type: string,
    cat: string,
    count_method: string,
    u_array?: { [index: number]: string | number | boolean },
    value?: number,
    transaction_id?: string
  ): void {
    const data = {
      allow_custom_scripts: false,
      send_to: `DC-${advertiser_id}/${type}/${cat}+${count_method}`
    };

    if (u_array) {
      for (const [i, val] of Object.entries(u_array)) {
        if (val) {
          data[`u${Number(i) + 1}`] = val;
        }
      }
    }

    if (count_method == "transactions") {
      data['value'] = value;
      data['transaction_id'] = transaction_id;
    }

    wspr.P("gtag", [
      "event",
      count_method == "transactions" ? "purchase" : "conversion",
      data
    ]);
  }

  private get_advertiser_id(advertiser_key: number): number {
    return this.get_floodlight_mapping(undefined, undefined)["advertisers"][
      advertiser_key
    ][0];
  }

  get_advertiser_markets(advertiser_key: number): Set<string> {
      const advertisers = this.get_floodlight_mapping(undefined, undefined)["advertisers"];
      const advertiser = advertisers[advertiser_key];
      
      const markets = Object.keys(advertiser[1]);
      
      return new Set(markets);
  }


  get_advertiser_countries(advertiser_key: number): Set<string> {
      const advertisers = this.get_floodlight_mapping(undefined, undefined)["advertisers"];
      const advertiser = advertisers[advertiser_key];
      
      const countries = Object.values(advertiser[1]).flat();
      
      return new Set(countries);
  }


  get_floodlight_count_method(char: string): string {
    const m = {
      u: "unique",
      s: "standard",
      p: "per_session",
      t: "transactions"
    };
    return m[char];
  }

  // END CONTENT INTERACTION FUNCTIONS

  // START PAGEVIEW FUNCTIONS
  pageview_floodlight(page: string): void {
    let page_params;
    [page, page_params] = page.split("?");
    // console.log("PAGE:>", page, page_params);

    const floodlight_mapping = this.get_floodlight_mapping(
      undefined,
      undefined
    );

    window.dataLayer = window.dataLayer || [];

    if (floodlight_mapping.pageloads) {
      const pageloads = floodlight_mapping.pageloads;

      for (const map_page in pageloads) {
        if (page.match(new RegExp(map_page, "ig"))) {
          for (const tag of pageloads[map_page]) {
            if (
              tag[0] == "" ||
              (page_params && page_params.match(new RegExp(tag[0], "ig")))
            ) {
              tag[0] == "";
              this.trigger_floodlight_tag({
                allowed_markets: this.get_advertiser_markets(tag[1]),
                allowed_countries: this.get_advertiser_countries(tag[1]),
                advertiser_id: this.get_advertiser_id(tag[1]),
                count_method: this.get_floodlight_count_method(tag[4]),
                type: tag[2],
                cat: tag[3],
                u_array: tag[5]
              });
            }
          }
        }
      }
    }
  }

  private get_floodlight_mapping(
    WRID?: string,
    UWRID?: string,
    REQUEST_BROCHURE?: string,
    REQUEST_IS_AGENT?: boolean,
    REQUEST_TYPE?: string,
    PURCHASE_REVENUE?: number,
    PURCHASE_TRANSACTION_ID?: string
  ): {
    advertisers: number[];
    conversions: {
      [key: string]: any // eslint-disable-line
    };
    pageloads: {
      [key: string]: any // eslint-disable-line
    };
    clicks: any // eslint-disable-line
  } {
    let floodlight_mapping = this.floodlight_mapping_template;

    const voyage_details = this.get_voyage_details();
    const currency = voyage_details.voyage_price_currency || voyage_details.lead_value_currency;

    const pairs = {
      AUID: sv.get_auid(),
      WRID: WRID,
      UWRID: UWRID,
      PAGE_CAT_1: this.get_page_cat1(),
      PAGE_TYPE: this.get_page_type(),
      PAGE_PATH: this.get_clean_page(),
      VOYAGE_CODE: voyage_details.code,
      VOYAGE_NAME: voyage_details.name,
      VOYAGE_DESTINATION_NAME: voyage_details.destination_name,
      VOYAGE_GUESTS:
        +voyage_details.adults_number + +voyage_details.kids_number,
      VOYAGE_SHIP_NAME: voyage_details.ship_name,
      VOYAGE_LEAD_VALUE: voyage_details.lead_value,
      VOYAGE_DEPARTURE_DATE: voyage_details.departure_date,
      VOYAGE_SUITE: voyage_details.suite,
      REQUEST_BROCHURE: REQUEST_BROCHURE,
      REQUEST_IS_AGENT:
        REQUEST_IS_AGENT === true || REQUEST_IS_AGENT === false
          ? REQUEST_IS_AGENT.toString()
          : "",
      REQUEST_TYPE: REQUEST_TYPE,
      PURCHASE_REVENUE: PURCHASE_REVENUE,
      PURCHASE_TRANSACTION_ID: PURCHASE_TRANSACTION_ID,
      CURRENCY: currency
    };

    // this.log("--->PAIRS: " + JSON.stringify(pairs))

    for (const k in pairs) {
      const r = new RegExp("\\[\\[" + k + "\\]\\]", "g");
      floodlight_mapping = floodlight_mapping.replace(
        r,
        pairs[k] ? pairs[k] : ""
      );
    }

    return JSON.parse(floodlight_mapping);
  }
}
